import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useMaterials } from '../../../features/products/hooks/useMetaData'
import { metaDataApi } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { Input } from '../../../shared/components'
import Button from '../../../shared/components/Button'

export const MaterialsTab = () => {
  const { data, isLoading } = useMaterials()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (name: string) => metaDataApi.createMaterial(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materials'] }),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => { await metaDataApi.updateMaterial(id, name) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['materials'] }); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteMaterial(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materials'] }),
  })

  const items = (data?.data ?? []) as unknown as Record<string, unknown>[]
  const columns = [{ key: 'name', label: 'Name' }]

  const renderAddForm = (onDone: () => void) => (
    <SimpleForm
      placeholder="Material name"
      isLoading={createMutation.isPending}
      onSubmit={async (val) => { await createMutation.mutateAsync(val); onDone() }}
      onCancel={onDone}
    />
  )

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <SimpleForm
      defaultValue={item.name as string}
      placeholder="Material name"
      isLoading={updateMutation.isPending}
      onSubmit={async (val) => updateMutation.mutateAsync({ id: item.id as string, name: val })}
      onCancel={onDone}
      isEdit
    />
  )

  return (
    <MetaTable
      title="Materials"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Material"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}

const SimpleForm = ({ defaultValue = '', placeholder, isLoading, onSubmit, onCancel, isEdit = false }: {
  defaultValue?: string
  placeholder: string
  isLoading: boolean
  onSubmit: (val: string) => Promise<void>
  onCancel: () => void
  isEdit?: boolean
}) => {
  const [value, setValue] = useState(defaultValue)
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Input label="Name" placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)} className="h-[38px]" />
      </div>
      <Button size="sm" onClick={() => onSubmit(value)} disabled={!value.trim() || isLoading} loading={isLoading} className="h-[38px]">
        {isEdit ? 'Update' : 'Save'}
      </Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
    </div>
  )
}
